import type * as React from "react";
import { type FC, type ReactNode } from "react";
import classNames from "classnames";
import Checkbox from "../../common/components/Checkbox";
import { IconType } from "../../common/components/icon/IconType";
import SavedConfigurationsTableEntry from "./SavedConfigurationsTableEntry";
import { type SavedConfigurationItem } from "./SavedConfigurationsPage";
import { LINEAR_CONFIGURATOR_URL } from "../../utils/url/staticUrls";
import { IMG_ORDER_SUCCESS } from "../../utils/url/staticResources";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";

interface Props {
  items: SavedConfigurationItem[];
  onSelectAll: () => void;
}

const SavedConfigurationsTable: FC<Props & JSX.IntrinsicElements["div"]> = (props) => {
  const { items, onSelectAll, className, ...rest } = props;

  const { t } = useTranslation();
  const tLabel = (key: string) => t(`page.savedLinearConfigurations.table.header.${key}`);

  const isAllSelected = items.length > 0 && items.every((item) => item.isSelected);

  return (
    <div className={classNames("scha__lcsc__table", className)} {...rest}>
      <div className="scha__lcsc__table__header">
        <HeaderCell first={true}>{tLabel("number")}</HeaderCell>
        <HeaderCell>{tLabel("configuration")}</HeaderCell>
        <HeaderCell>{tLabel("name")}</HeaderCell>
        <HeaderCell last={true}>
          <Checkbox checked={isAllSelected} id={"savedConfigurations_selectAll_checkbox"} onCheck={onSelectAll} />
        </HeaderCell>
      </div>

      <div className="scha__lcsc__table__body">
        {items.length === 0 ? <EmptyTableContents /> : <TableContents items={items} />}
      </div>
    </div>
  );
};

const HeaderCell: React.FC<{
  first?: boolean;
  last?: boolean;
  additionalInfo?: ReactNode;
  children?: ReactNode;
}> = ({ children, first, last, additionalInfo }) => {
  const cls = classNames({
    "px-3": !first,
    "px-0": first,
    "d-flex flex-direction-column flex-align-end": last,
  });
  return (
    <div className="d-flex flex-direction-column pb-2">
      <div className={cls}>{children}</div>
      {additionalInfo && <div className="scha__lc-saved-configs-page__additional-info">{additionalInfo}</div>}
    </div>
  );
};

type TableProps = Omit<Props, "onSelectAll">;
const EmptyTableContents: FC = () => {
  const { t } = useTranslation();

  const params = new URLSearchParams(window.location.search);
  const returnUrl = params.get("from") || LINEAR_CONFIGURATOR_URL;

  return (
    <div style={{ margin: "80px 0" }} className={"d-flex flex-direction-column flex-align-center"}>
      <p className={"display-small mt-0 mb-30"}>{t("page.savedLinearConfigurations.table.empty.upper")}</p>
      <p className={"title-medium mt-0 mb-30"}>{t("page.savedLinearConfigurations.table.empty.lower")}</p>
      <Button
        startIcon={<DefaultButtonIcon type={IconType.ArrowLeft} />}
        id={"savedConfigurations_returnToConfigurator"}
        href={returnUrl}
      >
        {t("page.savedLinearConfigurations.table.empty.button")}
      </Button>
      <img className={"my-60"} src={IMG_ORDER_SUCCESS} />
    </div>
  );
};

const TableContents: FC<TableProps> = ({ items }) => {
  return (
    <>
      {items.map((item, index) => (
        <SavedConfigurationsTableEntry key={item.code} index={index + 1} item={item} />
      ))}
    </>
  );
};

export default SavedConfigurationsTable;
